import type { ApiClient } from '@acies/platform';
import { Effect } from 'effect';
import type { SearchResult } from '../domain/search-results';
import { type ApiServices, runCall } from './runtime';
import { type SearchError, type SearchOptions, searchAt, searchCached, searchUrl } from './search';
import { callDependencies, type EnginePath } from './shared';

/** The resource segment the store's inventory items live under, which is what a stock position is a position of. */
const resource = 'component-inventory-items';

/**
 * What an inventory item search asks for, which is what any search asks for.
 *
 * `name` is the API's word for the text searched with, whichever collection it is put to. This one matches it against the item number — the key, `00AHJ4500` — and nothing else, so the field the user types into is the key field either way.
 */
export type SearchInventoryItemsOptions = SearchOptions;

/** Everything searching the store for inventory items can fail with. */
export type SearchInventoryItemsError = SearchError;

/** The calls against the store's inventory items. */
export interface InventoryItemCalls {
	/**
	 * Searches the store's inventory items by item number.
	 *
	 * Answers with at most `limit` items, so this is what the key field of a stock position calls as the user types rather than something to list the inventory with. The items beyond the limit are not reported: the caller asked for a shortlist and gets one.
	 */
	readonly searchInventoryItems: (options: SearchInventoryItemsOptions) => Effect.Effect<ReadonlyArray<SearchResult>, SearchInventoryItemsError>;
}

/**
 * Implements the inventory item calls over the client and store path every call shares.
 *
 * `typeof ApiClient.Service` is the shape `ApiClient` provides — the client with this API's host, bearer token, account header, auth failures and retry policy already applied.
 */
export function makeInventoryItemCalls(client: typeof ApiClient.Service, storePath: EnginePath): InventoryItemCalls {
	return {
		searchInventoryItems: (options) => storePath(resource).pipe(Effect.flatMap((path) => searchAt(client, searchUrl(path, options)))),
	};
}

/**
 * Searches the store's inventory items from outside Effect.
 *
 * The awaitable form of {@link searchInventoryItemsEffect}.
 */
export const searchInventoryItems = (limit: number, name: string): Promise<ReadonlyArray<SearchResult> | SearchInventoryItemsError> => runCall(() => searchInventoryItemsEffect(limit, name));

/**
 * Searches the store's inventory items, as an Effect over the Configurator's services.
 *
 * For the key field of a stock position, which forks its searches rather than awaiting them, so a search a later keystroke has overtaken is interrupted — see `SearchAutoComplete`.
 *
 * All this adds to the general `searchEffect` in `services/search.ts` is where the items live, which is the account's default engine, read per call — the same thing `searchConstructionModelsEffect` resolves for the models, see `services/construction-models.ts`.
 */
export const searchInventoryItemsEffect = (limit: number, name: string): Effect.Effect<ReadonlyArray<SearchResult>, SearchInventoryItemsError, ApiServices> => callDependencies.pipe(Effect.flatMap(({ storePath }) => storePath(resource).pipe(Effect.flatMap((path) => searchCached(path, { limit, name })))));
